import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { useLocations } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Truck, X, AlertTriangle, FileText, Eraser } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, AllergenTags } from "@/components/ui-bits";
import DateScopePicker, { scopeToParams, downloadCsv } from "@/components/DateScopePicker";
import SupervisorPinModal from "@/components/SupervisorPinModal";

export default function Dispatch() {
  const { params, selected } = useLocations();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", vehicle: "", driver: "", items: [], allergen_ack: false });
  const [scope, setScope] = useState({ mode: "today" });
  const [summary, setSummary] = useState(null); // null | { open, data }
  const [clearPinOpen, setClearPinOpen] = useState(false);

  const load = async () => {
    const [d, c, i] = await Promise.all([
      api.get("/dispatches", { params: { ...params(), ...scopeToParams(scope) } }),
      api.get("/customers"),
      api.get("/inventory", { params: params() }),
    ]);
    setRows(d.data); setCustomers(c.data); setInventory(i.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, scope]);

  const openEodSummary = async () => {
    try {
      const params2 = selected ? { location_id: selected } : {};
      const { data } = await api.get("/dispatches/end-of-day/summary", { params: params2 });
      setSummary({ open: true, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const doClearAll = async () => {
    try {
      const p = selected ? { location_id: selected } : {};
      const { data } = await api.post("/dispatches/clear-all", null, { params: p });
      toast.success(
        `Cleared ${data.deleted} dispatch record${data.deleted === 1 ? "" : "s"}`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        { _key: `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, packing_id: "", quantity_packs: 1 },
      ],
    }));
  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((x, idx) => idx===i ? { ...x, ...patch } : x) }));
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  // Roll up allergens from currently picked inventory items
  const aggregatedAllergens = useMemo(() => {
    const ids = form.items.map((x) => x.packing_id).filter(Boolean);
    const set = new Set();
    ids.forEach((id) => {
      const pk = inventory.find((p) => p.id === id);
      (pk?.allergens || []).forEach((a) => set.add(a));
    });
    return Array.from(set);
  }, [form.items, inventory]);

  const submit = async (e) => {
    e.preventDefault();
    if (aggregatedAllergens.length > 0 && !form.allergen_ack) {
      toast.error("Confirm allergen handover before dispatch");
      return;
    }
    try {
      await api.post("/dispatches", {
        ...form,
        location_id: selected || null,
        items: form.items.filter((x) => x.packing_id && Number(x.quantity_packs) > 0)
          .map((x) => ({ packing_id: x.packing_id, quantity_packs: Number(x.quantity_packs) })),
      });
      toast.success("Dispatch recorded");
      setOpen(false); setForm({ customer_id: "", vehicle: "", driver: "", items: [], allergen_ack: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Dispatch" eyebrow="Outbound · Customer deliveries">
        <Button
          variant="outline"
          className="h-12 px-4 gap-2"
          onClick={openEodSummary}
          data-testid="eod-summary-button"
          title="Generate today's end-of-day dispatch summary"
        >
          <FileText className="h-5 w-5" /> End-of-day summary
        </Button>
        <Button className="h-12 px-5 gap-2" onClick={()=>setOpen(true)} data-testid="new-dispatch-button">
          <Plus className="h-5 w-5" /> New dispatch
        </Button>
      </PageHeader>

      <Panel
        title={`Dispatches (${rows.length})`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <DateScopePicker value={scope} onChange={setScope} />
            {scope.mode === "history" && rows.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadCsv(
                    `dispatches-${scope.from_date}-to-${scope.to_date}.csv`,
                    rows,
                    [
                      { header: "Code", value: "dispatch_code" },
                      { header: "Customer", value: "customer_name" },
                      { header: "Date", value: "dispatch_date" },
                      { header: "Driver", value: "driver" },
                      { header: "Vehicle", value: "vehicle" },
                      { header: "Items", value: (r) => r.items?.length || 0 },
                      { header: "Allergens", value: (r) => (r.allergens || []).join(" · ") },
                    ]
                  )
                }
                data-testid="dispatch-export-csv"
              >
                Export CSV
              </Button>
            )}
            {isAdmin && rows.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setClearPinOpen(true)}
                data-testid="dispatch-clear-all"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5"
                title="Delete all dispatch records (admin PIN)"
              >
                <Eraser className="h-3.5 w-3.5" /> Clear all
              </Button>
            )}
          </div>
        }
      >
        {rows.length === 0 ? (
          <div className="py-10 text-center text-stone-500">No dispatches yet.</div>
        ) : (
          <table className="w-full" data-testid="dispatch-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr><th className="py-3">Code</th><th>Customer</th><th>Date</th><th>Driver / Vehicle</th><th>Allergens</th><th className="text-right">Items</th></tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-border h-16">
                  <td className="font-mono text-sm">{d.dispatch_code}</td>
                  <td className="font-medium">{d.customer_name}</td>
                  <td className="text-stone-600 text-sm">{d.dispatch_date?.slice(0,16).replace("T"," ")}</td>
                  <td className="text-stone-600 text-sm">{d.driver || "—"} · {d.vehicle || "—"}</td>
                  <td><AllergenTags items={d.allergens} /></td>
                  <td className="text-right tnum">{d.items?.length || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="dispatch-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Truck className="h-5 w-5" /> Record dispatch</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v)=>setForm({...form, customer_id: v})}>
                <SelectTrigger className="h-12" data-testid="dispatch-customer"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                <SelectContent>{customers.map((c)=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Driver</Label><Input className="h-12 mt-1" value={form.driver} onChange={(e)=>setForm({...form, driver: e.target.value})} data-testid="dispatch-driver" /></div>
              <div><Label>Vehicle</Label><Input className="h-12 mt-1" value={form.vehicle} onChange={(e)=>setForm({...form, vehicle: e.target.value})} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-dispatch-item"><Plus className="h-4 w-4 mr-1" /> Add line</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={it._key || `legacy-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-8">
                      <Select value={it.packing_id} onValueChange={(v)=>setItem(idx,{packing_id: v})}>
                        <SelectTrigger className="h-11"><SelectValue placeholder="Pick from inventory" /></SelectTrigger>
                        <SelectContent>
                          {inventory.map((p)=><SelectItem key={p.id} value={p.id}>{p.label_code} · {p.recipe_name} · {fmt2(p.remaining_packs)} avail{p.allergens?.length ? ` · ⚠ ${p.allergens.join(",")}` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" min="1" className="col-span-3 h-11" value={it.quantity_packs} onChange={(e)=>setItem(idx,{quantity_packs: e.target.value})} />
                    <Button type="button" size="icon" variant="ghost" onClick={()=>rmItem(idx)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                {form.items.length === 0 && <div className="text-sm text-stone-500">Add at least one line.</div>}
              </div>
            </div>

            {aggregatedAllergens.length > 0 && (
              <div className="border-2 border-amber-400 bg-amber-50 rounded-lg p-4" data-testid="allergen-warning">
                <div className="flex items-center gap-2 text-amber-900 font-display font-semibold">
                  <AlertTriangle className="h-5 w-5" /> Allergen alert
                </div>
                <div className="mt-1 text-sm text-amber-900">
                  This dispatch contains:{" "}
                  <span className="font-bold">{aggregatedAllergens.join(" · ")}</span>.
                  Confirm the customer has been informed and the allergen statement
                  is on every label before sending.
                </div>
                <label className="flex items-center gap-2 mt-3 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={form.allergen_ack}
                    onCheckedChange={(v) => setForm({ ...form, allergen_ack: !!v })}
                    data-testid="allergen-ack"
                  />
                  I confirm the allergen handover for this dispatch.
                </label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="dispatch-submit">Record dispatch</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!summary?.open} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent className="max-w-2xl" data-testid="eod-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileText className="h-5 w-5" /> End-of-day dispatch summary
            </DialogTitle>
          </DialogHeader>
          {summary?.data && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="rounded-lg bg-stone-50 dark:bg-stone-800 p-3 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500">Shift day</div>
                  <div className="font-display font-bold text-base">{summary.data.shift_date}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500">Dispatches</div>
                  <div className="font-display font-bold text-base tnum">{summary.data.total_dispatches}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500">Total packs</div>
                  <div className="font-display font-bold text-base tnum">{summary.data.total_packs}</div>
                </div>
              </div>

              {summary.data.allergens?.length > 0 && (
                <div className="border-2 border-amber-400 bg-amber-50 rounded-lg p-3 text-sm text-amber-900">
                  <strong>Allergens dispatched today:</strong>{" "}
                  {summary.data.allergens.join(" · ")}
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1.5">By customer</div>
                {summary.data.by_customer.length === 0 ? (
                  <div className="text-sm text-stone-500 py-4 text-center">No dispatches in this shift.</div>
                ) : (
                  <table className="w-full text-sm" data-testid="eod-by-customer">
                    <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                      <tr>
                        <th className="py-2">Customer</th>
                        <th>Dispatches</th>
                        <th>Packs</th>
                        <th>Codes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.data.by_customer.map((c) => (
                        <tr key={c.customer_id} className="border-t border-border">
                          <td className="py-2 font-medium">{c.customer_name}</td>
                          <td className="tnum">{c.dispatches}</td>
                          <td className="tnum">{c.packs}</td>
                          <td className="text-xs font-mono text-stone-500">{(c.codes || []).join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="text-[11px] text-stone-500">
                Generated {summary.data.generated_at?.slice(0, 19).replace("T", " ")} by{" "}
                {summary.data.generated_by}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSummary(null)}>Close</Button>
            <Button
              onClick={() => window.print()}
              data-testid="eod-summary-print"
            >
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupervisorPinModal
        open={clearPinOpen}
        title="Clear all dispatch records"
        description={`Delete ${rows.length} dispatch record${rows.length === 1 ? "" : "s"} from this scope. Traceability data (packing/batches) is untouched.`}
        roles={["admin"]}
        onClose={() => setClearPinOpen(false)}
        onVerified={() => {
          setClearPinOpen(false);
          doClearAll();
        }}
      />
    </div>
  );
}
